import { useState, useEffect, useRef } from 'react';
import { 
  ShoppingCart, 
  Search, 
  Filter, 
  Edit, 
  Trash2,
  CheckCircle,
  XCircle,
  Clock,
  AlertTriangle,
  ChevronDown,
  ChevronUp,
  DollarSign,
  CreditCard,
  ExternalLink,
  Weight,
  Package,
  X,
  MessageSquare,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';

interface ShoppingListItem {
  id: string;
  product_name: string;
  product_price: number;
  product_id: string;
  quantity: number;
  product_image?: string;
  product_weight?: string;
  payment_link?: string;
}

interface ShoppingList {
  payment_link?: string;
  id: string;
  name: string;
  status: 'active' | 'completed' | 'abandoned';
  created_at: string;
  updated_at: string;
  user?: {
    id?: string;
    name?: string;
    phone?: string;
    email?: string;
    bot_enabled?: boolean;
    atendimento?: boolean;
    email_woocomerce?: string;
  };
  seller?: {
    name?: string;
    phone?: string;
    email?: string;
    bot_enabled?: boolean;
  };
  shopping_list_items: ShoppingListItem[];
  woocommerce_order_id?: string;
  applied_subscription_id?: string;
  subscriptions?: Subscription;
}

interface Subscription {
  id: string;
  name: string;
  price_monthly: number;
  max_weight_kg: number;
  shipping_discount: number;
  handling_discount: number;
  benefits: string[];
  limitations: string[];
  active: boolean;
}

const Listas = () => {
  // Estados para modal de adicionar produto
  const [showAddProductModal, setShowAddProductModal] = useState(false);
  const [addProductForm, setAddProductForm] = useState({
    nome: '', tipo: 'simple', preco: '', descricao: '', short_description: '', categories: '', stock_quantity: '', sku: '', peso: '', list_id: '', qtd_id: '', woocommerce_product_id: ''
  });
  const [wooProducts, setWooProducts] = useState<any[]>([]);
  const [productSearchTerm, setProductSearchTerm] = useState('');
  const [searchingProducts, setSearchingProducts] = useState(false);
  const [selectedWooProduct, setSelectedWooProduct] = useState<any>(null);
  const [addProductLoading, setAddProductLoading] = useState(false);
  const [addProductError, setAddProductError] = useState('');
  const [addProductSuccess, setAddProductSuccess] = useState(false);
  const [showCreateProductForm, setShowCreateProductForm] = useState(false);
  const [createProductForm, setCreateProductForm] = useState({
    nome: '',
    preco: '',
    descricao: '',
    sku: '',
    peso: ''
  });
  const [creatingProduct, setCreatingProduct] = useState(false);
  const [taxaPorKGValue, setTaxaPorKGValue] = useState<{ data: any[] } | null>(null);
  const [produtosCSV, setProdutosCSV] = useState<any[]>([]);
  const [listDiscounts, setListDiscounts] = useState<{ [listId: string]: number }>({});

  useEffect(() => {
    // Buscar produtos do CSV ao montar o componente
    fetch('/api/produtos-csv')
      .then(res => res.json())
      .then(data => setProdutosCSV(data))
      .catch(err => console.error('Erro ao buscar produtos do CSV:', err));
  }, []);
  // Debounce para busca de produtos (1.5 segundos)
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);
  
  const searchWooProducts = async (search: string) => {
    if (!search || search.length < 2) {
      setWooProducts([]);
      return;
    }

    setSearchingProducts(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/woocommerce/search-products?search=${encodeURIComponent(search)}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      const data = await response.json();
      
      if (response.ok && data.success) {
        setWooProducts(data.products || []);
      } else {
        // Mostrar erro amigável
        setAddProductError(data.error || 'Erro ao buscar produtos');
        setWooProducts([]);
      }
    } catch (error) {
      console.error('Erro ao buscar produtos:', error);
      setAddProductError('Erro ao conectar com WooCommerce');
      setWooProducts([]);
    } finally {
      setSearchingProducts(false);
    }
  };
  
  const handleProductSearch = (search: string) => {
    setProductSearchTerm(search);
    
    // Limpar timer anterior
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
    
    // Limpar resultados enquanto digita
    setWooProducts([]);
    
    // Agendar nova busca após 1.5 segundos
    debounceTimerRef.current = setTimeout(() => {
      searchWooProducts(search);
    }, 1500);
  };

  // Calcular desconto progressivo baseado no peso total do carrinho
  const calculateProgressiveDiscount = (weight: number): number => {
    if (weight <= 5) return 5;
    if (weight <= 10) return 10;
    if (weight <= 20) return 15;
    if (weight <= 30) return 20;
    if (weight <= 40) return 25;
    if (weight <= 50) return 30;
    if (weight <= 60) return 35;
    if (weight <= 70) return 40;
    if (weight <= 80) return 45;
    return 50;
  };

  // Calcular peso total do carrinho (incluindo quantidades)
  // const calculateTotalCartWeight = (items: ShoppingListItem[]): number => {
  //   return items.reduce((total, item) => {
  //     const itemWeight = parseFloat(item.product_weight || '1') || 1;
  //     return total + (itemWeight * item.quantity);
  //   }, 0);
  // };

  // Aplicar desconto do clube UMA VEZ no carrinho todo
  const applyClubDiscount = (listId: string, items: ShoppingListItem[]) => {
    // Calcular peso total do carrinho
    const totalWeight = calculateTotalWeight(items);
    const discountPercent = calculateProgressiveDiscount(totalWeight);
    
    // Armazenar o desconto por lista
    setListDiscounts(prev => ({
      ...prev,
      [listId]: discountPercent
    }));
    
    console.log(`💰 Desconto aplicado à lista ${listId}: ${discountPercent}% (peso total: ${totalWeight.toFixed(2)} kg)`);
  };

  // Remover desconto da lista
  const removeClubDiscount = (listId: string) => {
    setListDiscounts(prev => {
      const newDiscounts = { ...prev };
      delete newDiscounts[listId];
      return newDiscounts;
    });
    console.log(`❌ Desconto removido da lista ${listId}`);
  };

  // Criar novo produto no WooCommerce
  const handleCreateProduct = async (e: React.FormEvent) => {
    e.preventDefault();
    setCreatingProduct(true);
    setAddProductError('');
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/woocommerce/create-product', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          name: createProductForm.nome,
          regular_price: createProductForm.preco,
          description: createProductForm.descricao,
          sku: createProductForm.sku,
          weight: createProductForm.peso
        })
      });
      
      const data = await response.json();
      
      if (response.ok && data.success) {
        // Produto criado com sucesso, selecionar automaticamente mantendo peso original
        selectCreatedProduct(data.product, createProductForm.peso);
        setShowCreateProductForm(false);
        setCreateProductForm({ nome: '', preco: '', descricao: '', sku: '', peso: '' });
      } else {
        setAddProductError(data.error || 'Erro ao criar produto');
      }
    } catch (err) {
      setAddProductError('Erro ao criar produto no WooCommerce');
    } finally {
      setCreatingProduct(false);
    }
  };

  // Resetar modal ao fechar
  const resetModal = () => {
    setShowAddProductModal(false);
    setSelectedWooProduct(null);
    setWooProducts([]);
    setProductSearchTerm('');
    setShowCreateProductForm(false);
    setAddProductError('');
    setAddProductSuccess(false);
    setAddProductForm({
      nome: '', tipo: 'simple', preco: '', descricao: '', short_description: '', 
      categories: '', stock_quantity: '', sku: '', peso: '', list_id: '', 
      qtd_id: '1', woocommerce_product_id: ''
    });
    setCreateProductForm({ nome: '', preco: '', descricao: '', sku: '', peso: '' });
  };

  // Selecionar produto do WooCommerce
  const selectWooProduct = (product: any) => {
    setSelectedWooProduct(product);
    console.log('Produto selecionado:', product);
    setAddProductForm({
      ...addProductForm,
      nome: product.name,
      preco: product.price,
      descricao: product.description || '',
      short_description: product.short_description || '',
      sku: product.sku || '',
      peso: product.weight || '1',
      stock_quantity: product.stock_quantity || '',
      qtd_id: '1',
      woocommerce_product_id: product.id.toString() // ID do produto do WooCommerce
    });
    setWooProducts([]);
    setProductSearchTerm('');
  };

  // Remover produto selecionado
  const removeSelectedProduct = () => {
    setSelectedWooProduct(null);
    setAddProductForm({
      nome: '', tipo: 'simple', preco: '', descricao: '', short_description: '', 
      categories: '', stock_quantity: '', sku: '', peso: '', list_id: addProductForm.list_id, 
      qtd_id: '1', woocommerce_product_id: ''
    });
  };

  // Selecionar produto recém-criado (mantém peso do formulário)
  const selectCreatedProduct = (product: any, originalWeight: string) => {
    setSelectedWooProduct(product);
    console.log('Produto criado selecionado:', product);
    console.log('Peso original do formulário:', originalWeight);
    setAddProductForm({
      ...addProductForm,
      nome: product.name,
      preco: product.price,
      descricao: product.description || '',
      short_description: product.short_description || '',
      sku: product.sku || '',
      peso: originalWeight, // Usar peso original do formulário
      stock_quantity: product.stock_quantity || '',
      qtd_id: '1',
      woocommerce_product_id: product.id.toString()
    });
  };

  const handleAddProduct = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!selectedWooProduct) {
      setAddProductError('Selecione um produto primeiro');
      return;
    }
    
    setAddProductLoading(true);
    setAddProductError('');
    setAddProductSuccess(false);
    
    try {
      const token = localStorage.getItem('token');
      
      console.log('🛒 Adicionando produto à lista:');
      console.log('- Peso do formulário:', addProductForm.peso);
      console.log('- Peso do WooCommerce:', selectedWooProduct.weight);
      console.log('- Peso que será enviado:', parseFloat(addProductForm.peso) || 1);
      
      const response = await fetch('/api/shopping-lists/add-product', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          list_id: addProductForm.list_id,
          woocommerce_product_id: selectedWooProduct.id,
          product_name: selectedWooProduct.name,
          product_price: parseFloat(selectedWooProduct.price),
          quantity: parseInt(addProductForm.qtd_id),
          product_sku: selectedWooProduct.sku,
          product_weight: parseFloat(addProductForm.peso) || 1, // Usar peso do formulário
          product_image: selectedWooProduct.images?.[0]?.src || ''
        })
      });
      
      const data = await response.json();
      
      if (response.ok && data.success) {
        setAddProductSuccess(true);
        setAddProductForm({ nome: '', tipo: 'simple', preco: '', descricao: '', short_description: '', categories: '', stock_quantity: '', sku: '', peso: '', list_id: '', qtd_id: '1', woocommerce_product_id: '' });
        setSelectedWooProduct(null);
        setProductSearchTerm('');
        
        // Recarregar listas
        setTimeout(() => { 
          setShowAddProductModal(false); 
          setAddProductSuccess(false);
          window.location.reload(); // Recarrega para mostrar o produto adicionado
        }, 1500);
      } else {
        setAddProductError(data.error || 'Erro ao adicionar produto');
      }
    } catch (err) {
      setAddProductError('Erro ao adicionar produto à lista');
    } finally {
      setAddProductLoading(false);
    }
  };

  const navigate = useNavigate();
    // Controle do modal de email woocomerce
  const [showEmailModal, setShowEmailModal] = useState<{
    userEmail: string;
    emailAtual: string;
    onConfirm: () => void;
  } | null>(null);
  const [emailWoocomerceInput, setEmailWoocomerceInput] = useState('');
  const [savingEmail, setSavingEmail] = useState(false);

  // Função para abrir modal e aguardar confirmação
  const ensureEmailWoocomerce = (userEmail: string, emailAtual: string, onConfirm: () => void) => {
    setEmailWoocomerceInput(emailAtual || '');
    setShowEmailModal({ userEmail, emailAtual, onConfirm });
  }; // sempre abre o modal, campo preenchido se houver

// Utilitário para validar e-mail
const isValidEmail = (email: string) => {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
};

  // Função para salvar email no backend e atualizar local
  const saveEmailWoocomerce = async () => {
    if (!showEmailModal || !emailWoocomerceInput.trim()) return;
    setSavingEmail(true);
    try {
      const response = await fetch(`/api/users/${encodeURIComponent(showEmailModal.userEmail)}/email_woocomerce`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ email_woocomerce: emailWoocomerceInput.trim() })
      });
      const result = await response.json();
      if (response.ok && result.success) {
        // Atualiza localmente o email_woocomerce
        setLists(prev => prev.map(list =>
          list.user?.email === showEmailModal.userEmail
            ? { ...list, user: { ...list.user, email_woocomerce: emailWoocomerceInput.trim() } }
            : list
        ));
        setShowEmailModal(null);
        setSavingEmail(false);
        // Executa o callback só depois de fechar o modal e resetar loading
        setTimeout(() => {
          showEmailModal.onConfirm();
        }, 0);
      } else {
        alert(result.error || 'Erro ao salvar e-mail');
        setSavingEmail(false);
      }
    } catch (err) {
      alert('Erro ao salvar e-mail');
      setSavingEmail(false);
    }
  };

  const [lists, setLists] = useState<ShoppingList[]>([]);
  const [subscriptions, setSubscriptions] = useState<Subscription[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [editingList, setEditingList] = useState<string | null>(null);
  const [editName, setEditName] = useState('');
  const [backendAvailable, setBackendAvailable] = useState(false);
  const [expandedLists, setExpandedLists] = useState<string[]>([]);
  const [creatingPaymentLink, setCreatingPaymentLink] = useState<string | null>(null);
  const [customAmount, setCustomAmount] = useState<number | null>(null);
  const [showCustomAmount, setShowCustomAmount] = useState<string | null>(null);
  // const [applyingSubscription, setApplyingSubscription] = useState<string | null>(null);
  const [removingSubscription, setRemovingSubscription] = useState<string | null>(null);
  // const [selectedSubscription, setSelectedSubscription] = useState<string>('');
  // const [applyTaxesOnPayment, setApplyTaxesOnPayment] = useState<boolean>(true);
  const [deletingItem, setDeletingItem] = useState<string | null>(null);
  // const [listsFrete, setListsFrete] = useState<any[]>([]);
  // const [listsHandling, setListsHandling] = useState<any[]>([]);
  
  useEffect(() => {
    loadLists();
    loadSubscriptions();
    loadConfigurations();
    console.log("ENTORUS")
    loadFretes();
    loadHandling();
    loadTaxaPorKG();
  }, []);

  const loadTaxaPorKG = async () => {
    console.log("Chamando fetch de taxa por kg...");
    try {
      const res = await fetch(`https://br.brazilianashop.com.br/wp-json/prc-tax/v1/taxa-kg`);
      console.log("Response status:", res.status);
      if (res.ok) {
        const data = await res.json();
        console.log("Taxa por kg recebida:", data);
        setTaxaPorKGValue(data);
      } else {
        console.error("Erro ao buscar taxa por kg:", res.statusText);
      }
    } catch (err) {
      console.error("Erro ao buscar taxa por kg:", err);
    }
  }

  const loadFretes = async () => {
    console.log("Chamando fetch de frete...");
    try {
      const res = await fetch(`/api/shipping/fee`);
      console.log("Response status:", res.status);
      if (res.ok) {
        const data = await res.json();
        console.log("Fretes recebidos:", data);
        // setListsFrete(data);
      } else {
        console.error("Erro ao buscar fretes:", res.statusText);
      }
    } catch (e) {
      console.error("Erro na requisição de frete:", e);
    }
  };
  
  const loadHandling = async () => {
    console.log("Chamando fetch de handling...");
    try {
      const res = await fetch(`/api/handling/fee`);
      console.log("Response status:", res.status);
      if (res.ok) {
        const data = await res.json();
        console.log("Handling recebidos:", data);
        // setListsHandling(data);
      } else {
        console.error("Erro ao buscar handling:", res.statusText);
      }
    } catch (e) {
      console.error("Erro na requisição de handling:", e);
    }
  };


  const loadLists = async () => {
    try {
      const response = await fetch('/api/shopping-lists', {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`
        }
      });
      if (response.ok) {
        const data = await response.json();
        setLists(data.data || []);
        setBackendAvailable(true);
      } else {
        setBackendAvailable(false);
      }
    } catch (error) {
      console.error('Error loading lists:', error);
      setBackendAvailable(false);
    } finally {
      setLoading(false);
    }
  };

  const loadSubscriptions = async () => {
    try {
      const response = await fetch('/api/subscriptions', {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`
        }
      });
      if (response.ok) {
        const data = await response.json();
        setSubscriptions(data.data?.filter((sub: Subscription) => sub.active) || []);
      }
    } catch (error) {
      console.error('Error loading subscriptions:', error);
    }
  };

  const loadConfigurations = async () => {
    try {
      const response = await fetch('/api/configurations', {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`
        }
      });
      if (response.ok) {
        const data = await response.json();
        console.log('Loaded configurations:', data.data);
        // setApplyTaxesOnPayment(data.data?.apply_taxes_on_payment !== false);
      }
    } catch (error) {
      console.error('Error loading configurations:', error);
    }
  };


  const abrirModal = async (listId: string, status: string, tipo: string, metodo: string) => {
    // Checar se vai mudar para completed
    console.log(metodo);
    const list = lists.find(l => l.id === listId);
    const userEmail = list?.user?.email;
    const emailAtual = list?.user?.email_woocomerce || '';
    if (userEmail) {
      return ensureEmailWoocomerce(userEmail, emailAtual, async () => {
        if(tipo === 'status'){
          if(list.status === 'completed'){
            alert('Lista já foi concluída');
            return;
          }
          await updateListStatus(listId, status);
        } else {
          await createPaymentLink(listId, metodo);
        }
      });
    }
  }

  const updateListStatus = async (listId: string, status: string) => {
    if (!backendAvailable) {
      alert('Backend não disponível. Esta é uma demonstração estática.');
      return;
    }
    try {
      const taxaPorKG = calculateTaxaPorKG2(listId);
      setLoading(true);
      console.log(status);
      const response = await fetch(`/api/shopping-lists/${listId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ updates: {status}, taxaPorKG })
      });

      if (response.ok) {
        loadLists();
      }
    } catch (error) {
      console.error('Error updating list status:', error);
    } finally {
      setLoading(false);
    }
  };

  const deleteListItem = async (listId: string, itemId: string) => {
    if (!backendAvailable) {
      alert('Backend não disponível. Esta é uma demonstração estática.');
      return;
    }

    setDeletingItem(itemId);

    try {
      const response = await fetch(`/api/shopping-list-items/${itemId}`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        // Update the local state to reflect the deletion
        setLists(lists.map(list => {
          if (list.id === listId) {
            return {
              ...list,
              shopping_list_items: list.shopping_list_items.filter(item => item.id !== itemId)
            };
          }
          return list;
        }));
      } else {
        alert('Erro ao remover item da lista');
      }
    } catch (error) {
      console.error('Error deleting list item:', error);
      alert('Erro ao remover item da lista');
    } finally {
      setDeletingItem(null);
    }
  };

  const startEditing = (list: ShoppingList) => {
    setEditingList(list.id);
    setEditName(list.name);
  };

  const cancelEditing = () => {
    setEditingList(null);
    setEditName('');
  };

  const toggleListExpansion = (listId: string) => {
    setExpandedLists(prev => 
      prev.includes(listId) 
        ? prev.filter(id => id !== listId) 
        : [...prev, listId]
    );
  };

  const filteredLists = lists.filter(list => {
    const matchesSearch = 
      list.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (list.user?.name?.toLowerCase().includes(searchTerm.toLowerCase()) || false) ||
      (list.user?.phone?.includes(searchTerm) || false);
    
    const matchesStatus = statusFilter === 'all' || list.status === statusFilter;
    
    return matchesSearch && matchesStatus;
  });

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(value);
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'active':
        return <Clock className="h-4 w-4 text-orange-500" />;
      case 'completed':
        return <CheckCircle className="h-4 w-4 text-green-500" />;
      case 'abandoned':
        return <XCircle className="h-4 w-4 text-red-500" />;
      default:
        return <Clock className="h-4 w-4 text-gray-500" />;
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'active':
        return 'Ativa';
      case 'completed':
        return 'Concluída';
      case 'abandoned':
        return 'Abandonada';
      default:
        return status;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'bg-orange-100 text-orange-800';
      case 'completed':
        return 'bg-green-100 text-green-800';
      case 'abandoned':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const calculateListTotal = (items: ShoppingListItem[]) => {
    return items.reduce((total, item) => total + (item.product_price * item.quantity), 0);
  };

  // Calcular total com descontos aplicados (desconto aplicado UMA VEZ no subtotal total)
  const calculateListTotalWithDiscounts = (listId: string, items: ShoppingListItem[]) => {
    // Calcular subtotal sem descontos
    const subtotal = items.reduce((total, item) => total + (item.product_price * item.quantity), 0);
    
    // Se houver desconto para esta lista, aplicar uma única vez
    const discountPercent = listDiscounts[listId] || 0;
    const discountAmount = subtotal * (discountPercent / 100);
    
    return subtotal - discountAmount;
  };

  const calculateTaxaPorKG = (item: ShoppingListItem[]) => {
    const peso = calculateTotalWeight(item);
    console.log("Validação");
    console.log(peso)
    console.log(taxaPorKGValue)
    console.log(!taxaPorKGValue || !('taxa_por_kg' in taxaPorKGValue))
    if (!taxaPorKGValue || !('taxa_por_kg' in taxaPorKGValue)) return 0;
    console.log(taxaPorKGValue?.taxa_por_kg)
    console.log(peso)

    // let embalagem = 0
    // if (peso > 0 && peso <= 2) {
    //   embalagem = 0.3; // 300g para embalagem
    // } else if (peso > 2 && peso <= 5) {
    //   embalagem = 0.75; // 750g para embalagem
    // } else if (peso > 5 && peso <= 10) {
    //   embalagem = 1.3; // 1.3kg para embalagem
    // } else if (peso > 10 && peso <= 15) {
    //   embalagem = 2; // 2kg para embalagem
    // } else if (peso > 15 && peso <= 20) {
    //   embalagem = 2.5; // 2.5kg para embalagem
    // } else if (peso > 20 && peso <= 30) {
    //   embalagem = 3; // 3kg para embalagem
    // } else if (peso > 30) {
    //   embalagem = 5; // 5kg para embalagem
    // }

    let peso2 = peso + 0;//embalagem;
    console.log(peso2)
    peso2 = parseInt(peso2 as any) + (peso2 % 1 > 0 ? 1 : 0);
    console.log(peso2)
    return peso2 * Number(taxaPorKGValue?.taxa_por_kg);
  };

  const calculateTaxaPorKG2 = (listId: string) => {
    const list = lists.find(l => l.id === listId);
    const peso = calculateTotalWeight(list?.shopping_list_items || []);
    console.log("Validação");
    console.log(peso)
    console.log(taxaPorKGValue)
    console.log(!taxaPorKGValue || !('taxa_por_kg' in taxaPorKGValue))
    if (!taxaPorKGValue || !('taxa_por_kg' in taxaPorKGValue)) return 0;
    console.log(taxaPorKGValue?.taxa_por_kg)
    console.log(peso)
    // let embalagem = 0
    // if (peso > 0 && peso <= 2) {
    //   embalagem = 0.3; // 300g para embalagem
    // } else if (peso > 2 && peso <= 5) {
    //   embalagem = 0.75; // 750g para embalagem
    // } else if (peso > 5 && peso <= 10) {
    //   embalagem = 1.3; // 1.3kg para embalagem
    // } else if (peso > 10 && peso <= 15) {
    //   embalagem = 2; // 2kg para embalagem
    // } else if (peso > 15 && peso <= 20) {
    //   embalagem = 2.5; // 2.5kg para embalagem
    // } else if (peso > 20 && peso <= 30) {
    //   embalagem = 3; // 3kg para embalagem
    // } else if (peso > 30) {
    //   embalagem = 5; // 5kg para embalagem
    // }

    let peso2 = peso + 0;//embalagem;
    console.log(peso2)
    peso2 = parseInt(peso2 as any) + (peso2 % 1 > 0 ? 1 : 0);
    console.log(peso2)
    return peso2 * Number(taxaPorKGValue?.taxa_por_kg);
  };

  const calculateTotalWeight = (items: ShoppingListItem[]) => {
    return items.reduce((total, item) => {
      // Busca produto pelo ID, considerando possíveis variações da chave
      const produto = produtosCSV.find((p) => {
        const idKey = Object.keys(p).find(
          k => k.replace(/\uFEFF/g, '').trim() === 'ID'
        );
        return idKey && String(p[idKey]) === String(item.product_id);
      });
      console.log("Produto");
      console.log(produto)
      console.log("Item");
      console.log(item)
      let pesoStr = produto && produto['Peso (kg)'] ? produto['Peso (kg)'] : '1'; // <-- Aqui: peso padrão 1
      pesoStr = pesoStr.replace(',', '.').trim();
      let pesoNum = parseFloat(pesoStr);
      if (pesoNum > 20) {
        pesoNum = pesoNum / 1000;
      }
      return total + ((isNaN(pesoNum) ? 1 : pesoNum) * item.quantity);
    }, 0);
  };
  // const calculateShippingFee = (items: ShoppingListItem[], subscription?: Subscription) => {
  //   // Se houver assinatura, retorna o desconto dela
  //   if (subscription) return subscription.shipping_discount;
  
  //   // Calcula o peso total dos itens
  //   const totalWeight = calculateTotalWeight(items);
  
  //   // Procura o intervalo correspondente ao peso
  //   const found = listsFrete.find(
  //     (interval) =>
  //       totalWeight >= interval.min_weight && totalWeight <= interval.max_weight
  //   );
  
  //   // Se encontrou, retorna o rate desse intervalo
  //   if (found) return found.rate;
  
  //   // Se não encontrou, pode retornar um valor padrão (exemplo: 0 ou um cálculo extra)
  //   return 0;
  // };

  const calculateICMSFee = (items: ShoppingListItem[]/*, subscription?: Subscription*/) => {
    // const frete = calculateShippingFee(items, subscription);
    const produtos = calculateListTotal(items);
    const ii = calculateIIFee(items);
    const total = (produtos + ii) * 0.20;//frete) * 0.20;
    return total;
  };

  // const calculateSeguroFee = (items: ShoppingListItem[]) => {
  //   const seguroFixo = 10;
  //   const produtos = calculateListTotal(items);
  //   const total = seguroFixo + (produtos * 0.07);
  //   return total;
  // };

  const calculateIIFee = (items: ShoppingListItem[]/*, subscription?: Subscription*/) => {
    console.log(items)
    // const frete = calculateShippingFee(items, subscription);
    const produtos = calculateListTotal(items);
    // const seguro = calculateSeguroFee(items);
    const base = produtos + 0;//frete + seguro;
    let total = 0;
    total = (base * 0.60);
    // if (base <= 50) {
    //   total = base * 0.20;
    // } else {
    //   total = (base * 0.60) - 20, 0;
    // }
    return total;
  };

  // const calculateHandlingFee = (items: ShoppingListItem[], subscription?: Subscription) => {
  //   // Calcula o peso total dos itens
  //   const totalWeight = calculateTotalWeight(items);
  
  //   // Procura o intervalo correspondente ao peso nos dados vindos do backend
  //   const found = listsHandling.find(
  //     (interval) =>
  //       totalWeight >= interval.min_weight && totalWeight <= interval.max_weight
  //   );
  
  //   // Se houver assinatura, retorna o desconto dela
  //   if (subscription) {
  //     return subscription.handling_discount;
  //   }
  
  //   // Se encontrou, retorna o rate desse intervalo
  //   if (found) return found.rate;
  
  //   // Se não encontrou, retorna um valor padrão (exemplo: 10)
  //   return 10;
  // };

  // const calculateTaxes = (items: ShoppingListItem[]) => {
  //   const subtotal = calculateListTotal(items);
  //   return subtotal * 0.085; // 8.5% tax rate
  // };

  // const calculateServiceFee = (items: ShoppingListItem[]) => {
  //   const subtotal = calculateListTotal(items);
  //   return subtotal * 0.25; // 25% service fee
  // };

  // const calculateBrazilianTaxes = (items: ShoppingListItem[], shipping: number, handlingFees: number) => {
  //   const subtotal = calculateListTotal(items);
  //   return (subtotal + shipping + handlingFees) * 0.8; // 80% of product + shipping + handling
  // };

  const calculateTotalBRL = (total: number) => {
    return total * 5.9; // Convert to BRL
  };

  const calculateTotalUSD = (listId: string, items: ShoppingListItem[]/*, subscription?: Subscription*/) => {
    // Usar subtotal com descontos aplicados
    const subtotal = calculateListTotalWithDiscounts(listId, items);
    // const serviceFee = calculateServiceFee(items);
    // const taxes = calculateTaxes(items);
    // const shipping = calculateShippingFee(items, subscription);
    // const handling = calculateHandlingFee(items, subscription);
    const taxaPorKG = calculateTaxaPorKG(items);
    
    // // Apply taxes based on configuration
    // const finalTaxes = applyTaxesOnPayment ? taxes : 0;
    
    // // Calculate Brazilian taxes
    // const brazilianTaxes = calculateBrazilianTaxes(items, shipping, handling);
    
    // // Only include Brazilian taxes in the total if we're applying taxes
    // const finalBrazilianTaxes = applyTaxesOnPayment ? brazilianTaxes : 0;
    
    return subtotal + taxaPorKG; //+ serviceFee + finalTaxes + shipping + handling + finalBrazilianTaxes;
  };

  const getAppliedSubscription = (list: ShoppingList): Subscription | undefined => {
    if (!list.applied_subscription_id) return undefined;
    return subscriptions.find(sub => sub.id === list.applied_subscription_id);
  };

  // const canApplySubscription = (list: ShoppingList, subscription: Subscription) => {
  //   const totalWeight = calculateTotalWeight(list.shopping_list_items || []);
  //   return totalWeight <= subscription.max_weight_kg;
  // };

  // const completeOrder = async (listId: string) => {
  //   try {
  //     setLoading(true);
  //     const response = await fetch(`/api/shopping-lists/${listId}/complete`, {
  //       method: 'POST'
  //     });
      
  //     if (response.ok) {
  //       const result = await response.json();
  //       console.log('Order completed:', result);
  //       alert('Pedido criado com sucesso no WooCommerce!');
  //       loadLists();
  //     } else {
  //       const error = await response.json();
  //       alert(`Erro ao completar pedido: ${error.error || 'Erro desconhecido'}`);
  //     }
  //   } catch (error) {
  //     console.error('Error completing order:', error);
  //     alert('Erro ao completar pedido');
  //   } finally {
  //     setLoading(false);
  //   }
  // };

  // Utility to get logged-in userId from localStorage (if stored)
const getCurrentUserId = (): string | null => {
  try {
    const userStr = localStorage.getItem('user');
    if (userStr) {
      const user = JSON.parse(userStr);
      return user.id || null;
    }
  } catch {}
  return null;
};

const createPaymentLink = async (listId: string, metodo: string) => {

  try {
    setCreatingPaymentLink(listId);

    const payload: any = {};
    if (showCustomAmount === listId && customAmount) {
      payload.amount_brl = customAmount;
    }

    // Inject seller_id from logged-in user
    const sellerId = getCurrentUserId();
    if (sellerId) {
      payload.seller_id = sellerId;
    }
    if (metodo) {
      payload.payment_method = metodo;
    }

    const response = await fetch(`/api/shopping-lists/${listId}/payment-link`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${localStorage.getItem('token')}`
      },
      body: JSON.stringify(payload)
    });

    if (response.ok) {
      const result = await response.json();
      const paymentLink = result.data?.payment_link;
      // Atualiza a lista localmente para mostrar o botão imediatamente
      setLists(prevLists =>
        prevLists.map(list =>
          list.id === listId
            ? { ...list, payment_link: paymentLink }
            : list
        )
      );
      setShowCustomAmount(null);
      setCustomAmount(null);
    } else {
      const error = await response.json();
      alert(`Erro ao criar link de pagamento: ${error.error || 'Erro desconhecido'}`);
    }
  } catch (error) {
    console.error('Error creating payment link:', error);
    alert('Erro ao criar link de pagamento');
  } finally {
    setCreatingPaymentLink(null);
  }
};

  const updateListName = async (listId: string, name: string) => {
    if (!backendAvailable) {
      alert('Backend não disponível. Esta é uma demonstração estática.');
      return;
    }

    try {
      const response = await fetch(`/api/shopping-lists/${listId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ name })
      });

      if (response.ok) {
        setEditingList(null);
        setEditName('');
        loadLists();
      }
    } catch (error) {
      console.error('Error updating list name:', error);
    }
  };

  const deleteList = async (listId: string) => {
    if (!backendAvailable) {
      alert('Backend não disponível. Esta é uma demonstração estática.');
      return;
    }

    if (!confirm('Tem certeza que deseja excluir esta lista?')) {
      return;
    }

    try {
      const response = await fetch(`/api/shopping-lists/${listId}`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        loadLists();
      }
    } catch (error) {
      console.error('Error deleting list:', error);
    }
  };

  // const applySubscription = async (listId: string, subscriptionId: string) => {
  //   if (!backendAvailable) {
  //     alert('Backend não disponível. Esta é uma demonstração estática.');
  //     return;
  //   }

  //   setApplyingSubscription(listId);

  //   try {
  //     const response = await fetch(`/api/shopping-lists/${listId}/apply-subscription`, {
  //       method: 'POST',
  //       headers: {
  //         'Content-Type': 'application/json',
  //         Authorization: `Bearer ${localStorage.getItem('token')}`
  //       },
  //       body: JSON.stringify({ subscription_id: subscriptionId })
  //     });

  //     const result = await response.json();

  //     if (response.ok) {
  //       alert(`Assinatura aplicada com sucesso!\n\n${result.message}`);
  //       loadLists();
  //     } else {
  //       alert(`Erro ao aplicar assinatura:\n\n${result.error}\n\n${result.details || ''}`);
  //     }
  //   } catch (error) {
  //     console.error('Error applying subscription:', error);
  //     alert('Erro ao aplicar assinatura');
  //   } finally {
  //     setApplyingSubscription(null);
  //     setSelectedSubscription('');
  //   }
  // };

  const removeSubscription = async (listId: string) => {
    if (!backendAvailable) {
      alert('Backend não disponível. Esta é uma demonstração estática.');
      return;
    }

    if (!confirm('Tem certeza que deseja remover a assinatura desta lista?')) {
      return;
    }

    setRemovingSubscription(listId);

    try {
      const response = await fetch(`/api/shopping-lists/${listId}/remove-subscription`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}`
        }
      });

      const result = await response.json();

      if (response.ok) {
        alert(`Assinatura removida com sucesso!\n\n${result.message}`);
        loadLists();
      } else {
        alert(`Erro ao remover assinatura:\n\n${result.error}`);
      }
    } catch (error) {
      console.error('Error removing subscription:', error);
      alert('Erro ao remover assinatura');
    } finally {
      setRemovingSubscription(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <>
      {/* MODAL EMAIL WOOCOMERCE */}
      {showEmailModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
          <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-sm">
            <h2 className="text-lg font-bold mb-2">E-mail de cadastro WooCommerce</h2>
            <p className="mb-4 text-gray-700 text-sm">Informe o e-mail de cadastro do cliente no WooCommerce para continuar.</p>
            <input
              type="email"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 mb-3 focus:ring-2 focus:ring-blue-500"
              value={emailWoocomerceInput}
              onChange={e => setEmailWoocomerceInput(e.target.value)}
              placeholder="cliente@email.com"
              required
              autoFocus
            />
            <div className="flex justify-end space-x-2">
              <button
                className="px-4 py-2 bg-gray-200 rounded-lg text-gray-700 hover:bg-gray-300"
                onClick={() => setShowEmailModal(null)}
                disabled={savingEmail}
              >Cancelar</button>
              <button
                className="px-4 py-2 bg-blue-600 rounded-lg text-white hover:bg-blue-700 disabled:opacity-50"
                onClick={saveEmailWoocomerce}
                disabled={savingEmail || !isValidEmail(emailWoocomerceInput)}
              >{savingEmail ? 'Salvando...' : 'Salvar'}</button>
            </div>
          </div>
        </div>
      )}
      <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Listas de Compras</h1>
          <p className="text-gray-600 mt-1">Gerencie todas as listas de compras dos usuários</p>
        </div>
      </div>

      {/* MODAL DE ADICIONAR PRODUTO */}
      {showAddProductModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
          <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-lg relative max-h-[90vh] overflow-y-auto">
            <button
              className="absolute top-2 right-2 text-gray-400 hover:text-red-600"
              onClick={resetModal}
            >
              <X className="h-6 w-6" />
            </button>
            <h2 className="text-lg font-bold mb-2">Adicionar Produto</h2>
            
            {/* Campo de busca do WooCommerce */}
            <div className="mb-4 pb-4 border-b">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                🔍 Buscar produto do WooCommerce
              </label>
              <input
                type="text"
                placeholder="Digite o nome do produto..."
                className="w-full border rounded px-3 py-2"
                value={productSearchTerm}
                onChange={(e) => handleProductSearch(e.target.value)}
              />
              
              {/* Lista de produtos encontrados */}
              {searchingProducts && (
                <div className="mt-2 text-sm text-gray-500">Buscando produtos...</div>
              )}
              
              {wooProducts.length > 0 && (
                <div className="mt-2 max-h-60 overflow-y-auto border rounded">
                  {wooProducts.map((product) => (
                    <div
                      key={product.id}
                      className="p-3 hover:bg-gray-50 cursor-pointer border-b last:border-b-0"
                      onClick={() => selectWooProduct(product)}
                    >
                      <div className="flex items-center gap-3">
                        {product.images?.[0]?.src && (
                          <img 
                            src={product.images[0].src} 
                            alt={product.name}
                            className="w-12 h-12 object-cover rounded"
                          />
                        )}
                        <div className="flex-1">
                          <div className="font-medium text-sm">{product.name}</div>
                          <div className="text-xs text-gray-500">
                            ${product.price} • SKU: {product.sku || 'N/A'}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              
              {selectedWooProduct && (
                <div className="mt-2 p-3 bg-green-50 border border-green-200 rounded">
                  <div className="flex items-center justify-between">
                    <div className="text-sm">
                      ✓ Produto selecionado: <strong>{selectedWooProduct.name}</strong>
                      <br />
                      <span className="text-gray-600">${selectedWooProduct.price} • SKU: {selectedWooProduct.sku || 'N/A'}</span>
                    </div>
                    <button
                      type="button"
                      onClick={removeSelectedProduct}
                      className="text-red-600 hover:text-red-800 text-sm font-medium"
                    >
                      ✕ Remover
                    </button>
                  </div>
                </div>
              )}
              
              {/* Mostrar opção de cadastrar produto apenas quando não há produto selecionado */}
              {!selectedWooProduct && (
                <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded">
                  <p className="text-sm text-blue-700 mb-2">Quer cadastrar um novo produto?</p>
                  <button
                    type="button"
                    onClick={() => setShowCreateProductForm(!showCreateProductForm)}
                    className="text-sm bg-blue-600 text-white px-3 py-1 rounded hover:bg-blue-700"
                  >
                    {showCreateProductForm ? 'Cancelar' : '➕ Cadastrar novo produto'}
                  </button>
                </div>
              )}
            </div>

            {/* Formulário de criar novo produto */}
            {showCreateProductForm && (
              <div className="mb-4 pb-4 border-b bg-blue-50 p-4 rounded">
                <h3 className="font-semibold text-sm mb-3">Cadastrar novo produto no WooCommerce</h3>
                <form onSubmit={handleCreateProduct} className="space-y-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Nome</label>
                    <input
                      type="text"
                      required
                      value={createProductForm.nome}
                      onChange={(e) => setCreateProductForm(prev => ({ ...prev, nome: e.target.value }))}
                      className="w-full border rounded px-2 py-1 text-sm"
                      placeholder="Nome do produto"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Preço (USD)</label>
                    <input
                      type="number"
                      step="0.01"
                      required
                      value={createProductForm.preco}
                      onChange={(e) => setCreateProductForm(prev => ({ ...prev, preco: e.target.value }))}
                      className="w-full border rounded px-2 py-1 text-sm"
                      placeholder="0.00"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Descrição</label>
                    <textarea
                      value={createProductForm.descricao}
                      onChange={(e) => setCreateProductForm(prev => ({ ...prev, descricao: e.target.value }))}
                      className="w-full border rounded px-2 py-1 text-sm min-h-[60px]"
                      placeholder="Descrição do produto"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">SKU</label>
                      <input
                        type="text"
                        value={createProductForm.sku}
                        onChange={(e) => setCreateProductForm(prev => ({ ...prev, sku: e.target.value }))}
                        className="w-full border rounded px-2 py-1 text-sm"
                        placeholder="SKU"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Peso (kg)</label>
                      <input
                        type="number"
                        step="0.01"
                        value={createProductForm.peso}
                        onChange={(e) => setCreateProductForm(prev => ({ ...prev, peso: e.target.value }))}
                        className="w-full border rounded px-2 py-1 text-sm"
                        placeholder="0.00"
                      />
                    </div>
                  </div>
                  <button
                    type="submit"
                    disabled={creatingProduct}
                    className="w-full bg-blue-600 text-white py-2 rounded text-sm hover:bg-blue-700 disabled:opacity-50"
                  >
                    {creatingProduct ? 'Criando...' : 'Criar e Adicionar'}
                  </button>
                </form>
              </div>
            )}

            <form onSubmit={handleAddProduct}>
              <div className="grid grid-cols-1 gap-4">
                {selectedWooProduct && (
                  <>
                    <div className="p-4 bg-gray-50 rounded-lg">
                      <div className="flex items-center gap-3">
                        {selectedWooProduct.images?.[0]?.src && (
                          <img 
                            src={selectedWooProduct.images[0].src} 
                            alt={selectedWooProduct.name}
                            className="w-20 h-20 object-cover rounded"
                          />
                        )}
                        <div className="flex-1">
                          <h3 className="font-semibold text-gray-900">{selectedWooProduct.name}</h3>
                          <p className="text-sm text-gray-600">${selectedWooProduct.price}</p>
                          <p className="text-xs text-gray-500">SKU: {selectedWooProduct.sku || 'N/A'}</p>
                        </div>
                      </div>
                    </div>
                    
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Quantidade</label>
                        <input 
                          type="number" 
                          min="1" 
                          value={addProductForm.qtd_id} 
                          onChange={e => setAddProductForm(prev => ({ ...prev, qtd_id: e.target.value }))} 
                          required 
                          className="w-full border rounded px-3 py-2"
                          placeholder="Digite a quantidade"
                        />
                    </div>
                  </>
                )}
                
                {!selectedWooProduct && (
                  <div className="text-center py-8 text-gray-500">
                    👆 Busque e selecione um produto acima
                  </div>
                )}
              </div>
              <div className="flex justify-end mt-6">
                <button
                  type="button"
                  className="px-4 py-2 bg-gray-200 rounded-lg text-gray-700 hover:bg-gray-300 mr-2"
                  onClick={resetModal}
                >Cancelar</button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 rounded-lg text-white hover:bg-blue-700 disabled:opacity-50"
                  disabled={addProductLoading || !selectedWooProduct}
                >{addProductLoading ? 'Adicionando...' : 'Adicionar à Lista'}</button>
              </div>
              {addProductError && (
                <div className="mt-2 text-red-600 text-sm">{addProductError}</div>
              )}
              {addProductSuccess && (
                <div className="mt-2 text-green-600 text-sm">Produto cadastrado com sucesso!</div>
              )}
            </form>
          </div>
        </div>
      )}


      {/* Backend Warning */}
      {!backendAvailable && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-6">
          <div className="flex items-start">
            <AlertTriangle className="h-6 w-6 text-yellow-600 mr-3 mt-0.5" />
            <div>
              <h3 className="text-lg font-semibold text-yellow-800 mb-2">
                Dados Não Disponíveis
              </h3>
              <p className="text-yellow-700">
                Esta é uma demonstração estática. As listas de compras aparecerão aqui quando o backend estiver implantado e funcionando.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center">
            <div className="p-3 rounded-full bg-blue-100">
              <ShoppingCart className="h-6 w-6 text-blue-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Total</p>
              <p className="text-2xl font-bold text-gray-900">{lists.length}</p>
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center">
            <div className="p-3 rounded-full bg-orange-100">
              <Clock className="h-6 w-6 text-orange-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Ativas</p>
              <p className="text-2xl font-bold text-gray-900">
                {lists.filter(l => l.status === 'active').length}
              </p>
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center">
            <div className="p-3 rounded-full bg-green-100">
              <CheckCircle className="h-6 w-6 text-green-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Concluídas</p>
              <p className="text-2xl font-bold text-gray-900">
                {lists.filter(l => l.status === 'completed').length}
              </p>
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center">
            <div className="p-3 rounded-full bg-purple-100">
              <Package className="h-6 w-6 text-purple-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Com Assinatura</p>
              <p className="text-2xl font-bold text-gray-900">
                {lists.filter(l => l.applied_subscription_id).length}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Buscar listas..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          <div className="flex items-center space-x-2">
            <Filter className="h-4 w-4 text-gray-400" />
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="all">Todos os Status</option>
              <option value="active">Ativas</option>
              <option value="completed">Concluídas</option>
              <option value="abandoned">Abandonadas</option>
            </select>
          </div>
        </div>
      </div>

      {/* Lists */}
      <div className="space-y-4">
        {!backendAvailable ? (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12 text-center">
            <ShoppingCart className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">Backend Não Disponível</h3>
            <p className="mt-1 text-sm text-gray-500">
              Implante o backend para ver as listas de compras
            </p>
          </div>
        ) : filteredLists.length === 0 ? (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12 text-center">
            <ShoppingCart className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">Nenhuma lista encontrada</h3>
            <p className="mt-1 text-sm text-gray-500">
              {searchTerm || statusFilter !== 'all' 
                ? 'Tente ajustar os filtros de busca' 
                : 'As listas aparecerão aqui quando os usuários criarem'}
            </p>
          </div>
        ) : (
          filteredLists.map((list) => {
            // Prioriza assinatura vinda do backend (list.subscriptions)
            const appliedSubscription = list.subscriptions || getAppliedSubscription(list);
            
            return (
              <div key={list.id} className="bg-white rounded-xl shadow-sm border border-gray-200">
                <div className="flex items-start justify-between p-6 border-b border-gray-200">
                  <div className="flex-1">
                    {editingList === list.id ? (
                      <div className="flex items-center space-x-2">
                        <input
                          type="text"
                          value={editName}
                          onChange={(e) => setEditName(e.target.value)}
                          className="text-lg font-semibold border border-gray-300 rounded px-2 py-1"
                          onKeyPress={(e) => {
                            if (e.key === 'Enter') {
                              updateListName(list.id, editName);
                            }
                          }}
                        />
                        <button
                          onClick={() => updateListName(list.id, editName)}
                          className="text-green-600 hover:text-green-800"
                        >
                          <CheckCircle className="h-4 w-4" />
                        </button>
                        <button
                          onClick={cancelEditing}
                          className="text-red-600 hover:text-red-800"
                        >
                          <XCircle className="h-4 w-4" />
                        </button>
                      </div>
                    ) : (
                      <>
                        <h3 className="text-lg font-semibold text-gray-900">{list.name}</h3>
                        <div className="flex items-center gap-2 mt-2">
                          {list.user?.email && (
                            <button
                              type="button"
                              className="inline-flex items-center px-3 py-1 bg-gray-100 text-blue-700 border border-blue-200 rounded-lg hover:bg-blue-50"
                              onClick={() => navigate(`/usuarios/${list.user?.id}`)}
                            >
                              <MessageSquare className="h-4 w-4 mr-1" />
                              Abrir chat
                            </button>
                          )}
                          <button
                            className="inline-flex items-center px-3 py-1 bg-gray-100 text-blue-700 border border-blue-200 rounded-lg hover:bg-blue-50"
                            onClick={() => {
                              // Resetar tudo antes de abrir o modal
                              resetModal();
                              // Definir a lista e abrir o modal
                              setAddProductForm(prev => ({ ...prev, list_id: list.id }));
                              setShowAddProductModal(true);
                            }}
                          >
                            Adicionar produto
                          </button>
                          {list.payment_link && (
                            <a
                              href={list.payment_link}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center px-3 py-1 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                            >
                              <ExternalLink className="h-4 w-4 mr-1" />
                              Acessar Link de Pagamento
                            </a>
                          )}
                        </div>
                      </>
                    )}
                    <div className="flex items-center space-x-4 mt-2">
                    <p className="text-sm text-gray-600">
                      <strong>{list.user?.name || '-'}</strong> - {list.user?.phone || '-'}
                      {list.user?.email && ` - ${list.user.email}`}
                    </p>
                    {/* Tag de Atendimento */}
                    {typeof list.user?.bot_enabled !== 'undefined' && typeof list.user?.atendimento !== 'undefined' && (
                      <>
                        {list.user.bot_enabled ? (
                          <span
                            className="inline-flex items-center px-3 py-1 border border-transparent text-xs font-semibold rounded-md text-pink-700 bg-pink-100"
                            style={{ letterSpacing: 1 }}
                          >
                            FORA DE ATENDIMENTO
                          </span>
                        ) : list.user.atendimento ? (
                          <span
                            className="inline-flex items-center px-3 py-1 border border-transparent text-xs font-semibold rounded-md text-green-700 bg-green-100"
                            style={{ letterSpacing: 1 }}
                          >
                            EM ATENDIMENTO{list.seller?.name ? ` POR ${list.seller.name.toUpperCase()}` : ''}
                          </span>
                        ) : (
                          <span
                            className="inline-flex items-center px-3 py-1 border border-transparent text-xs font-semibold rounded-md text-red-700 bg-red-100"
                            style={{ letterSpacing: 1 }}
                          >
                            AGUARDANDO ATENDIMENTO
                          </span>
                        )}
                      </>
                    )}
                      <div className="flex items-center space-x-1">
                        {getStatusIcon(list.status)}
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(list.status)}`}>
                          {getStatusText(list.status)}
                        </span>
                      </div>
                      {list.woocommerce_order_id && (
                        <span className="px-2 py-1 text-xs font-medium rounded-full bg-purple-100 text-purple-800">
                          Pedido #{list.woocommerce_order_id}
                        </span>
                      )}
                      {appliedSubscription && (
                        <span className="px-2 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-800 flex items-center">
                          <Package className="h-3 w-3 mr-1" />
                          {appliedSubscription.name}
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-gray-500 mt-1">
                      Criada em {formatDate(list.created_at)}
                    </p>
                  </div>
                  
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => toggleListExpansion(list.id)}
                      className="p-2 text-gray-400 hover:text-blue-600"
                      title={expandedLists.includes(list.id) ? "Recolher" : "Expandir"}
                    >
                      {expandedLists.includes(list.id) ? (
                        <ChevronUp className="h-4 w-4" />
                      ) : (
                        <ChevronDown className="h-4 w-4" />
                      )}
                    </button>
                    <button
                      onClick={() => startEditing(list)}
                      disabled={!backendAvailable}
                      className="p-2 text-gray-400 hover:text-blue-600 disabled:opacity-50"
                      title="Editar nome"
                    >
                      <Edit className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => deleteList(list.id)}
                      disabled={!backendAvailable}
                      className="p-2 text-gray-400 hover:text-red-600 disabled:opacity-50"
                      title="Excluir lista"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>

                {/* Status Actions */}
                {backendAvailable && (
                  <div className="flex items-center justify-between px-6 py-3 bg-gray-50 border-b border-gray-200">
                    <div className="flex items-center space-x-2">
                      <span className="text-sm font-medium text-gray-700">Alterar status:</span>
                      <button
                        onClick={() => updateListStatus(list.id, 'active')}
                        className={`px-3 py-1 text-xs font-medium rounded-full ${
                          list.status === 'active' 
                            ? 'bg-orange-100 text-orange-800' 
                            : 'bg-gray-100 text-gray-600 hover:bg-orange-50'
                        }`}
                        disabled={loading}
                      >
                        Ativa
                      </button>
                      <button
                        onClick={() => {abrirModal(list.id, 'completed', 'status', 'mercadopago')}}
                        className={`px-3 py-1 text-xs font-medium rounded-full ${
                          list.status === 'completed' 
                            ? 'bg-green-100 text-green-800' 
                            : 'bg-gray-100 text-gray-600 hover:bg-green-50'
                        }`}
                        disabled={loading}
                      >
                        Concluída
                      </button>
                      <button
                        onClick={() => updateListStatus(list.id, 'abandoned')}
                        className={`px-3 py-1 text-xs font-medium rounded-full ${
                          list.status === 'abandoned' 
                            ? 'bg-red-100 text-red-800' 
                            : 'bg-gray-100 text-gray-600 hover:bg-red-50'
                        }`}
                        disabled={loading}
                      >
                        Abandonada
                      </button>
                    </div>

                    {/* Subscription and Payment Actions */}
                    <div className="flex items-center space-x-2">
                      {/* Apply Subscription Dropdown */}
                      {/* {subscriptions.length > 0 && !appliedSubscription && (
                        <div className="flex items-center space-x-2">
                          <select
                            value={selectedSubscription}
                            onChange={(e) => setSelectedSubscription(e.target.value)}
                            className="text-sm border border-gray-300 rounded-lg px-2 py-1"
                          >
                            <option value="">Aplicar Assinatura</option>
                            {subscriptions.map((subscription) => {
                              const canApply = canApplySubscription(list, subscription);
                              return (
                                <option 
                                  key={subscription.id} 
                                  value={subscription.id}
                                  disabled={!canApply}
                                >
                                  {subscription.name} - ${subscription.price_monthly}/mês
                                  {!canApply && ' (Peso excede limite)'}
                                </option>
                              );
                            })}
                          </select>
                          
                          {selectedSubscription && (
                            <button
                              onClick={() => applySubscription(list.id, selectedSubscription)}
                              disabled={applyingSubscription === list.id}
                              className="px-3 py-1 bg-purple-600 text-white text-sm rounded-lg hover:bg-purple-700 disabled:opacity-50"
                            >
                              {applyingSubscription === list.id ? 'Aplicando...' : 'Aplicar'}
                            </button>
                          )}
                        </div>
                      )} */}

                      {/* Remove Subscription Button */}
                      {appliedSubscription && (
                        <button
                          onClick={() => removeSubscription(list.id)}
                          disabled={removingSubscription === list.id}
                          className="flex items-center px-3 py-1 bg-red-600 text-white text-sm rounded-lg hover:bg-red-700 disabled:opacity-50"
                          title="Remover assinatura"
                        >
                          <X className="h-4 w-4 mr-1" />
                          {removingSubscription === list.id ? 'Removendo...' : 'Remover Assinatura'}
                        </button>
                      )}

                      {/* Payment Link Button */}
                      {showCustomAmount === list.id ? (
                        <div className="flex items-center space-x-2">
                          <input
                            type="number"
                            value={customAmount || ''}
                            onChange={(e) => setCustomAmount(parseFloat(e.target.value) || 0)}
                            placeholder="Valor em BRL"
                            className="w-32 px-2 py-1 text-sm border border-gray-300 rounded-lg"
                          />
                          <button
                            onClick={async () => {
                              try {
                                const token = localStorage.getItem('token');
                                const apiUrl = import.meta.env.VITE_API_URL || window.location.origin;
                                
                                // Salvar total_brl na lista primeiro
                                await fetch(`${apiUrl}/api/shopping-lists/${list.id}`, {
                                  method: 'PUT',
                                  headers: {
                                    'Authorization': `Bearer ${token}`,
                                    'Content-Type': 'application/json'
                                  },
                                  body: JSON.stringify({ updates: { total_brl: customAmount } })
                                });
                                
                                // Gerar link de checkout
                                const res = await fetch(`${apiUrl}/api/checkout/create-session`, {
                                  method: 'POST',
                                  headers: {
                                    'Authorization': `Bearer ${token}`,
                                    'Content-Type': 'application/json'
                                  },
                                  body: JSON.stringify({ shopping_list_id: list.id })
                                });

                                const data = await res.json();
                                
                                if (data.success) {
                                  navigator.clipboard.writeText(data.data.checkout_url);
                                  alert(`Link de checkout copiado!\n\n${data.data.checkout_url}`);
                                  setShowCustomAmount(null);
                                  loadLists(); // Recarregar listas
                                } else {
                                  alert('Erro ao gerar link: ' + data.error);
                                }
                              } catch (error) {
                                console.error('Erro ao gerar link:', error);
                                alert('Erro ao gerar link de checkout');
                              }
                            }}
                            disabled={creatingPaymentLink === list.id}
                            className="px-3 py-1 bg-green-600 text-white text-sm rounded-lg hover:bg-green-700 disabled:opacity-50"
                          >
                            {creatingPaymentLink === list.id ? 'Criando...' : 'Confirmar'}
                          </button>
                          <button
                            onClick={() => setShowCustomAmount(null)}
                            className="px-3 py-1 bg-gray-200 text-gray-700 text-sm rounded-lg hover:bg-gray-300"
                          >
                            Cancelar
                          </button>
                        </div>
                      ) : (
                        <div className="flex gap-2">
                          
                          <button
                            onClick={() => {
                              const userEmail = list?.user?.email;
                              const emailAtual = list?.user?.email_woocomerce || '';
                              
                              if (userEmail) {
                                // Abrir modal de email e depois gerar checkout
                                ensureEmailWoocomerce(userEmail, emailAtual, () => {
                                  if (showCustomAmount === list.id) {
                                    // Gerar checkout com valor personalizado
                                    (async () => {
                                      try {
                                        const token = localStorage.getItem('token');
                                        const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3001';
                                        
                                        // Salvar total_brl na lista primeiro
                                        await fetch(`${apiUrl}/api/shopping-lists/${list.id}`, {
                                          method: 'PUT',
                                          headers: {
                                            'Authorization': `Bearer ${token}`,
                                            'Content-Type': 'application/json'
                                          },
                                          body: JSON.stringify({ updates: { total_brl: customAmount } })
                                        });
                                        
                                        const res = await fetch(`${apiUrl}/api/checkout/create-session`, {
                                          method: 'POST',
                                          headers: {
                                            'Authorization': `Bearer ${token}`,
                                            'Content-Type': 'application/json'
                                          },
                                          body: JSON.stringify({ shopping_list_id: list.id })
                                        });

                                        const data = await res.json();
                                        
                                        if (data.success) {
                                          navigator.clipboard.writeText(data.data.checkout_url);
                                          alert(`Link de checkout copiado!\n\n${data.data.checkout_url}`);
                                          setShowCustomAmount(null);
                                        } else {
                                          alert('Erro ao gerar link: ' + data.error);
                                        }
                                      } catch (error) {
                                        console.error('Erro ao gerar link:', error);
                                        alert('Erro ao gerar link de checkout');
                                      }
                                    })();
                                  } else {
                                    setShowCustomAmount(list.id);
                                    setCustomAmount(calculateTotalBRL(calculateTotalUSD(list.id, list.shopping_list_items || [])));
                                  }
                                });
                              }
                            }}
                            disabled={creatingPaymentLink === `${list.id}-appmax`}
                            className="flex items-center px-3 py-1 bg-blue-700 text-white text-sm rounded-lg hover:bg-blue-800 disabled:opacity-50"
                          >
                            <CreditCard className="h-4 w-4 mr-1" />
                            {creatingPaymentLink === `${list.id}-appmax` ? 'Gerando...' : 'Gerar Checkout'}
                          </button>
                        </div>
                      )}
                      
                      {list.woocommerce_order_id && (
                        <a
                          href={`${list.woocommerce_order_id}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center px-3 py-1 bg-purple-600 text-white text-sm rounded-lg hover:bg-purple-700"
                        >
                          <ExternalLink className="h-4 w-4 mr-1" />
                          Ver Pedido
                        </a>
                      )}
                    </div>
                  </div>
                )}

                {/* List Summary */}
                <div className="flex justify-between items-center p-4 bg-gray-50 border-b border-gray-200">
                  <div className="flex items-center space-x-4">
                    <span className="text-sm font-medium text-gray-700">
                      {list.shopping_list_items?.length || 0} itens
                    </span>
                    <div className="flex items-center text-sm text-gray-600">
                      <Weight className="h-4 w-4 mr-1" />
                      <span>{calculateTotalWeight(list.shopping_list_items || []).toFixed(2)}kg</span>
                    </div>
                    {appliedSubscription && (
                      <div className="flex items-center text-sm text-blue-600">
                        <Package className="h-4 w-4 mr-1" />
                        <span>Limite: {appliedSubscription.max_weight_kg}kg</span>
                      </div>
                    )}
                  </div>
                  <div className="text-right">
                    <span className="text-sm text-gray-500">Total:</span>
                    <span className="ml-2 text-lg font-bold text-gray-900">
                      {calculateListTotalWithDiscounts(list.id, list.shopping_list_items || []) !== calculateListTotal(list.shopping_list_items || []) ? (
                        <>
                          <span className="line-through text-gray-400 mr-2 text-sm">{formatCurrency(calculateListTotal(list.shopping_list_items || []))}</span>
                          <span className="text-green-600">{formatCurrency(calculateListTotalWithDiscounts(list.id, list.shopping_list_items || []))}</span>
                        </>
                      ) : (
                        formatCurrency(calculateListTotal(list.shopping_list_items || []))
                      )}
                    </span>
                    {appliedSubscription && (
                      <div className="text-xs text-blue-600">
                        Com desconto {appliedSubscription.name}
                      </div>
                    )}
                  </div>
                </div>

                {/* Items */}
                {expandedLists.includes(list.id) && list.shopping_list_items && list.shopping_list_items.length > 0 && (
                  <div className="p-6">
                    <h4 className="text-sm font-medium text-gray-900 mb-3">
                      Itens ({list.shopping_list_items.length})
                    </h4>
                    <div className="overflow-x-auto">
                      {/* Botão de Desconto - Aparece uma única vez */}
                      <div className="mb-4 flex justify-between items-center">
                        <h4 className="text-sm font-medium text-gray-900">Itens da Lista</h4>
                        {listDiscounts[list.id] ? (
                          <div className="flex items-center gap-2">
                            <span className="text-xs bg-green-100 text-green-800 px-3 py-1 rounded-full font-medium">
                              🎉 Desconto de {listDiscounts[list.id]}% aplicado
                            </span>
                            <button
                              onClick={() => removeClubDiscount(list.id)}
                              className="text-xs bg-red-100 text-red-700 px-3 py-1 rounded hover:bg-red-200 transition"
                              title="Remover desconto do carrinho"
                            >
                              Remover Desconto
                            </button>
                          </div>
                        ) : (
                          <button
                            onClick={() => applyClubDiscount(list.id, list.shopping_list_items)}
                            className="text-xs bg-blue-100 text-blue-700 px-3 py-1 rounded hover:bg-blue-200 transition font-medium"
                            title="Aplicar desconto do clube baseado no peso total"
                          >
                            💰 Aplicar Desconto
                          </button>
                        )}
                      </div>

                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"></th>
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Produto</th>
                            <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">QTD</th>
                            <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Preço</th>
                            <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Total</th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {list.shopping_list_items.map((item) => (
                            <tr key={item.id}>
                              <td className="px-3 py-2 whitespace-nowrap">
                                <button
                                  onClick={() => deleteListItem(list.id, item.id)}
                                  disabled={deletingItem === item.id}
                                  className="text-red-500 hover:text-red-700 disabled:opacity-50"
                                  title="Remover item"
                                >
                                  <X className="h-4 w-4" />
                                </button>
                              </td>
                              <td className="px-3 py-2 whitespace-nowrap">
                                <div className="flex items-center">
                                  {item.product_image && (
                                    <img
                                      src={item.product_image}
                                      alt={item.product_name}
                                      className="h-10 w-10 rounded-md object-cover mr-3"
                                    />
                                  )}
                                  <div className="text-sm font-medium text-gray-900">
                                    {item.product_name}
                                  </div>
                                </div>
                              </td>
                              <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500 text-center">
                                {item.quantity}
                              </td>
                              <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-900 text-right">
                                {formatCurrency(item.product_price)}
                              </td>
                              <td className="px-3 py-2 whitespace-nowrap text-sm font-medium text-gray-900 text-right">
                                {formatCurrency(item.product_price * item.quantity)}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    {/* Pricing Summary */}
                    <div className="mt-6 border-t border-gray-200 pt-4">
                      <h5 className="text-sm font-medium text-gray-900 mb-3 flex items-center">
                        <DollarSign className="h-4 w-4 mr-1 text-green-600" />
                        Resumo do Orçamento
                        {appliedSubscription && (
                          <span className="ml-2 px-2 py-1 text-xs bg-blue-100 text-blue-700 rounded-full">
                            Com {appliedSubscription.name}
                          </span>
                        )}
                      </h5>
                      
                      <div className="bg-gray-50 rounded-lg p-4">
                        <div className="space-y-2">
                          <div className="flex justify-between text-sm">
                            <span className="text-gray-600">Subtotal:</span>
                            <span className="font-medium">
                              {calculateListTotalWithDiscounts(list.id, list.shopping_list_items) !== calculateListTotal(list.shopping_list_items) ? (
                                <>
                                  <span className="line-through text-gray-400 mr-2">{formatCurrency(calculateListTotal(list.shopping_list_items))}</span>
                                  <span className="text-green-600 font-bold">{formatCurrency(calculateListTotalWithDiscounts(list.id, list.shopping_list_items))}</span>
                                </>
                              ) : (
                                formatCurrency(calculateListTotal(list.shopping_list_items))
                              )}
                            </span>
                          </div>
                          
                          {/* <div className="flex justify-between text-sm">
                            <span className="text-gray-600">Taxa de Serviço:</span>
                            <span>{formatCurrency(calculateServiceFee(list.shopping_list_items))}</span>
                          </div> */}
                          <div className="flex justify-between text-sm">
                            <span className="text-gray-600">Taxa de Serviço:</span>
                            <span>{formatCurrency(calculateTaxaPorKG(list.shopping_list_items))}</span>
                          </div>
                          
                          {/* <div className="flex justify-between text-sm">
                            <span className="text-gray-600">
                              Impostos (8.5%):
                              {!applyTaxesOnPayment && (
                                <span className="text-orange-600 ml-1">(apenas informativo)</span>
                              )}
                            </span>
                            <span className={!applyTaxesOnPayment ? "text-orange-600" : ""}>
                              {formatCurrency(calculateTaxes(list.shopping_list_items))}
                            </span>
                          </div> */}

                          {/* <div className="flex justify-between text-sm">
                            <span className="text-gray-600">
                              Taxa de redirecionamento:
                              {appliedSubscription && (
                                <span className="text-blue-600 ml-1">(taxa fixa)</span>
                              )}
                            </span>
                            <span>{formatCurrency(calculateHandlingFee(list.shopping_list_items, appliedSubscription))}</span>
                          </div> */}
                          
                          <div className="flex justify-between text-sm">
                            <span className="text-gray-600">
                              Taxa de Frete:
                              {/* {appliedSubscription && (
                                <span className="text-blue-600 ml-1">(taxa fixa)</span>
                              )} */}
                            </span>
                            {/* <span>{formatCurrency(calculateShippingFee(list.shopping_list_items, appliedSubscription))}</span> */}
                            <span>Grátis</span>
                          </div>

                          {/* <div className="flex justify-between text-sm">
                            <span className="text-gray-600">Seguro:
                              <span className="text-orange-600 ml-1">(apenas informativo)</span>
                            </span>
                            <span className="text-orange-600">{formatCurrency(calculateSeguroFee(list.shopping_list_items))}</span>
                          </div> */}

                          <div className="flex justify-between text-sm">
                            <span className="text-gray-600">ICMS:
                              <span className="text-orange-600 ml-1">(estimativa a pagar à receita federal)</span>
                            </span>
                            <span className="text-orange-600">{formatCurrency(calculateICMSFee(list.shopping_list_items/*, appliedSubscription*/))}</span>
                          </div>

                          <div className="flex justify-between text-sm">
                            <span className="text-gray-600">Imposto de importação:
                              <span className="text-orange-600 ml-1">(estimativa a pagar à receita federal)</span>
                            </span>
                            <span className="text-orange-600">{formatCurrency(calculateIIFee(list.shopping_list_items))}</span>
                          </div>

                          {/*<div className="flex justify-between text-sm">
                            <span className="text-gray-600">
                              Impostos Brasil (80% do produto + frete + handling):
                              {!applyTaxesOnPayment && (
                                <span className="text-orange-600 ml-1">(estimativa a pagar à receita federal)</span>
                              )}
                            </span>
                            <span className={!applyTaxesOnPayment ? "text-orange-600" : ""}>
                              {formatCurrency(calculateBrazilianTaxes(
                                list.shopping_list_items,
                                calculateShippingFee(list.shopping_list_items, appliedSubscription),
                                calculateHandlingFee(list.shopping_list_items, appliedSubscription)
                              ))}
                            </span>
                          </div>*/}

                          <div className="flex justify-between text-sm">
                            <span className="text-gray-600 flex items-center">
                              <Weight className="h-4 w-4 mr-1" />
                              Peso Total:
                            </span>
                            <span>{calculateTotalWeight(list.shopping_list_items).toFixed(2)}kg</span>
                          </div>

                          <div className="flex justify-between font-bold text-blue-700 mt-2">
                            <span>Total em USD </span>
                            <span>
                              {new Intl.NumberFormat('en-US', {
                                style: 'currency',
                                currency: 'USD'
                              }).format(calculateTotalUSD(list.id, list.shopping_list_items/*, appliedSubscription*/))}
                            </span>
                          </div>

                          <div className="flex justify-between font-bold text-green-700 mt-2">
                            <span>Total em BRL (5.9x):</span>
                            <span>
                              {new Intl.NumberFormat('pt-BR', {
                                style: 'currency',
                                currency: 'BRL'
                              }).format(calculateTotalBRL(calculateTotalUSD(list.id, list.shopping_list_items/*, appliedSubscription*/)))}
                            </span>
                          </div>

                          {appliedSubscription && (
                            <div className="border-t border-blue-200 pt-2 mt-2 bg-blue-50 rounded p-2">
                              <div className="text-sm text-blue-700">
                                <div className="font-medium mb-1">Benefícios da Assinatura {appliedSubscription.name}:</div>
                                <ul className="text-xs space-y-1">
                                  {appliedSubscription.benefits.map((benefit, index) => (
                                    <li key={index} className="flex items-start">
                                      <CheckCircle className="h-3 w-3 text-blue-600 mr-1 mt-0.5 flex-shrink-0" />
                                      {benefit}
                                    </li>
                                  ))}
                                </ul>
                                {appliedSubscription.limitations.length > 0 && (
                                  <div className="mt-2">
                                    <div className="font-medium mb-1">Limitações:</div>
                                    <ul className="text-xs space-y-1">
                                      {appliedSubscription.limitations.map((limitation, index) => (
                                        <li key={index} className="flex items-start">
                                          <AlertTriangle className="h-3 w-3 text-orange-600 mr-1 mt-0.5 flex-shrink-0" />
                                          {limitation}
                                        </li>
                                      ))}
                                    </ul>
                                  </div>
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>
    </div>
    </>
  );
};

export default Listas;